import { useMemo } from 'react';
import useTranslation from 'next-translate/useTranslation';

// Lib
import { ScheduledReportColumns } from '@/lib/reports/tableConfigs/tableColumns';

// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';
import useFetchReports from '@/utils/hooks/swr/reports/useFetchReports';

// Components
import TableList from '@/components/pages/reports/components/Table/TableList';
import TableFilters from '@/components/pages/reports/components/TableFilters';
import TableWrapper from '@/components/pages/reports/components/Table/TableWrapper';
import TablePagination from '@/components/pages/reports/components/Table/TablePagination';
import DeleteReportModal from '@/components/pages/reports/components/Modal/DeleteReportModal';
import DeleteAllVisibleReportsModal from '@/components/pages/reports/components/Modal/DeleteAllVisibleReportsModal';

const ScheduledReports = () => {
    const { t } = useTranslation();
    const { isOpen, toggle, params, setParams } = useToggle();
    const { isOpen: isOpenDeleteAllVisible, toggle: toggleDeleteAllVisible } = useToggle();
    const { reports, mutateReports, requestParams, setRequestParams, errorReports, isLoading } =
        useFetchReports({
            isScheduled: true,
            params: { per_page: 5, page: 1 },
            options: {
                refreshInterval: 30000 // add refresh interval to get reports when they're ready
            }
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const columnsScheduled = useMemo(() => ScheduledReportColumns(t, toggle, setParams), []);

    return (
        <div className="@container">
            <TableFilters requestParams={requestParams} setRequestParams={setRequestParams} />
            {reports?.data && reports?.data?.length ? (
                <div className="text-right">
                    <button
                        className="mb-2.5 text-xs text-icoBlue"
                        onClick={toggleDeleteAllVisible}>
                        {t('reports:general.delete_all')}
                    </button>
                </div>
            ) : null}
            <TableWrapper>
                <TableList
                    data={reports?.data ?? []}
                    isLoading={isLoading}
                    errorReports={errorReports}
                    mutateReports={mutateReports}
                    requestParams={requestParams}
                    setRequestParams={setRequestParams}
                    columns={columnsScheduled}
                />
                <TablePagination
                    reports={reports}
                    isLoading={isLoading}
                    errorReports={errorReports}
                    requestParams={requestParams}
                    setRequestParams={setRequestParams}
                />
            </TableWrapper>
            <DeleteAllVisibleReportsModal
                isOpen={isOpenDeleteAllVisible}
                toggle={toggleDeleteAllVisible}
                reports={reports?.data ?? []}
                mutateReports={mutateReports}
            />
            <DeleteReportModal
                isOpen={isOpen}
                toggle={toggle}
                params={params}
                mutateReports={mutateReports}
            />
        </div>
    );
};

export default ScheduledReports;
